import { AttioValuationItemQueryResponse } from '../attio.types';
import { AttioValuationDetailResponse } from '../shared-types';
import { AttioCompanyService } from './AttioCompanyService';
import { AttioConsultantService } from './AttioConsultantService';
import { AttioDealService } from './AttioDealService';
import { AttioEngagementService } from './AttioEngagementService';
import { AttioPeopleService } from './AttioPeopleService';
import { BaseAttioService } from './BaseAttioService';

export class AttioValuationDetailService extends BaseAttioService {
  private readonly companyService: AttioCompanyService;
  private readonly peopleService: AttioPeopleService;
  private readonly dealService: AttioDealService;
  private readonly engagementService: AttioEngagementService;
  private readonly consultantService: AttioConsultantService;

  constructor() {
    super('AttioValuationDetailService');
    this.companyService = new AttioCompanyService();
    this.peopleService = new AttioPeopleService();
    this.dealService = new AttioDealService();
    this.engagementService = new AttioEngagementService();
    this.consultantService = new AttioConsultantService();
  }

  /**
   * Get a single valuation item with related data
   */
  async getValuationDetail(valuationRecordId: string): Promise<AttioValuationDetailResponse> {
    try {
      const response = await this.get<AttioValuationItemQueryResponse>(
        this.endpoints.retrieveValuationByRecordId(valuationRecordId)
      );

      const { data } = response;
      const { values: valuationData } = data;

      // Extract target record IDs
      const valuedEntityRecordId = valuationData.valued_entity?.[0]?.target_record_id;
      const valuationLeadRecordId = valuationData.valuation_lead_94?.[0]?.target_record_id;
      const dealRecordId = valuationData.deal?.[0]?.target_record_id;
      const otherConsultantRecordId = valuationData.other_consultant?.[0]?.target_record_id;

      const valuationUrl = data.web_url;

      // Initialize base response
      const baseResponse: AttioValuationDetailResponse = {
        valuedEntity: {
          valuedEntityId: valuedEntityRecordId || '-',
          companyName: valuationData.identifier?.[0]?.value || '-',
          primaryContact: '-',
          primaryContactId: undefined,
          category: valuationData.engagement_category?.[0]?.option?.title || '-',
          jobTitle: '-',
          salesRep: '',
          salesRepImageSrc: '',
          companyUrl: undefined,
          valuedEntityNotice: undefined,
          primaryContactNotice: undefined,
        },
        valuedInformation: {
          valuationDate: valuationData.valuation_date?.[0]?.value || '-',
          valuationName: valuationData.identifier?.[0]?.value || '-',
          bookingType: valuationData.booking_type?.[0]?.option?.title || '-',
          bookingDate: valuationData.booking_date?.[0]?.value || '-',
          valuationLeadName: '-',
          valuationLeadImageSrc: '-',
          valuationId: valuationData.record_id?.[0]?.value || '-',
          renewalDate: '-',
          valuationUrl,
          otherConsultantName: '-',
          otherConsultantImageSrc: '-',
          otherConsultantEmail: '-',
          valuationNotice: valuationData.notice?.[0]?.value || undefined,
          valuationFee: valuationData.valuation_fee?.[0]?.currency_value || 0,
          valuationNetValue: '-',
          valuationEngagementManager: '-',
          valuationEngagementManagerImageSrc: '-',
          valuationEngagementManagerRole: '-',
        },
        deal: {
          dealId: dealRecordId || '-',
          valuationDate: valuationData.valuation_date?.[0]?.value || '-',
          dealTotal: '-',
          dealStage: '-',
          engagementManager: '-',
          engagementManagerImageSrc: '-',
          engagementManagerRole: '-',
          dealUrl: undefined,
          dealNotice: undefined,
          due: '-',
          net: '-',
          contractStatus: '-',
          ELURL: undefined,
          signedELURL: undefined,
          dealDate: '-',
          dealStageOptions: [],
        },
        status: valuationData.stage?.[0]?.option?.title || '-',
        statusId: valuationData.stage?.[0]?.option?.id?.option_id || undefined,
      };

      try {
        // Get company data
        if (valuedEntityRecordId) {
          const companyData = await this.companyService.getCompanyData(valuedEntityRecordId);
          baseResponse.valuedEntity.companyName = companyData.companyName;
          baseResponse.valuedEntity.companyUrl = companyData.companyUrl;
          baseResponse.valuedEntity.valuedEntityNotice = companyData.notes?.[0]?.value || undefined;

          // Get sales rep details
          if (companyData.salesRepId) {
            const salesRepData = await this.engagementService.getEngagementManager(companyData.salesRepId);
            baseResponse.valuedEntity.salesRep = salesRepData.name;
            baseResponse.valuedEntity.salesRepImageSrc = salesRepData.imageSrc;
          }

          // Get primary contact details
          if (companyData.primaryContactId) {
            const primaryContactData = await this.peopleService.getPeopleData(companyData.primaryContactId);
            baseResponse.valuedEntity.primaryContact = primaryContactData.name;
            baseResponse.valuedEntity.primaryContactId = companyData.primaryContactId;
            baseResponse.valuedEntity.jobTitle = primaryContactData.jobTitle;
            baseResponse.valuedEntity.primaryContactNotice = primaryContactData.notes?.[0]?.value || undefined;
          }
        }

        // Get valuation engagement manager details
        if (valuationData.engagement_manager?.[0]?.target_record_id) {
          const valuationEngagementManagerData = await this.engagementService.getValuationEngagementManager(valuationData.engagement_manager?.[0]?.target_record_id);
          baseResponse.valuedInformation.valuationEngagementManager = valuationEngagementManagerData.name;
          baseResponse.valuedInformation.valuationEngagementManagerImageSrc = valuationEngagementManagerData.imageSrc;
          baseResponse.valuedInformation.valuationEngagementManagerRole = valuationEngagementManagerData.role;
        }

        // Get valuation lead details
        if (valuationLeadRecordId) {
          const valuationLeadData = await this.consultantService.getOtherConsultant(valuationLeadRecordId);
          baseResponse.valuedInformation.valuationLeadName = valuationLeadData.name;
          baseResponse.valuedInformation.valuationLeadImageSrc = valuationLeadData.imageSrc;
        }

        // Get deal details
        if (dealRecordId) {
          const dealData = await this.dealService.getDealData(dealRecordId);
          baseResponse.deal.dealUrl = dealData.dealUrl;
          baseResponse.deal.dealName = dealData.dealName;
          baseResponse.deal.dealTotal = dealData.dealTotal;
          baseResponse.deal.dealStage = dealData.dealStage;
          baseResponse.deal.due = dealData.due;
          baseResponse.deal.dealDate = dealData.dealDate;
          baseResponse.deal.net = dealData.net;
          baseResponse.valuedInformation.valuationNetValue = dealData.net || '-';
          baseResponse.deal.dealNotice = dealData.notice?.[0]?.value || undefined;

          const dealStageOptions = await this.dealService.getDealStatuses();
          baseResponse.deal.dealStageOptions = dealStageOptions.data;

          // Get deal contract details
          const dealContractData = await this.dealService.getDealContractDataWithSpecificList(dealRecordId);

          if (dealContractData) {

            const contractData = dealContractData.data[0]?.entry_values;

            baseResponse.deal.contractStatus = contractData?.contract_status[0]?.status?.title || undefined;
            baseResponse.deal.ELURL = contractData?.doc_link[0]?.value || undefined;
            baseResponse.deal.signedELURL = dealData.signedPDF ?? contractData?.signed_doc_link?.[0]?.value;
          }

          // Get engagement manager details
          if (dealData.engagementManagerId) {
            const engagementManagerData = await this.engagementService.getEngagementManager(
              dealData.engagementManagerId
            );
            baseResponse.deal.engagementManager = engagementManagerData.name;
            baseResponse.deal.engagementManagerImageSrc = engagementManagerData.imageSrc;
          }
        }

        // Get other consultant details
        if (otherConsultantRecordId) {
          const otherConsultantData = await this.consultantService.getOtherConsultant(otherConsultantRecordId);
          baseResponse.valuedInformation.otherConsultantName = otherConsultantData.name;
          baseResponse.valuedInformation.otherConsultantImageSrc = otherConsultantData.imageSrc;
          baseResponse.valuedInformation.otherConsultantEmail = otherConsultantData.email;
        }

        return baseResponse;
      } catch (error) {
        console.warn('Error fetching additional data, returning base response:', error);
        return baseResponse;
      }
    } catch (error) {
      this.handleError(error, 'getValuationDetail');
      throw error; // Re-throw after handling
    }
  }

  async getValuationByRecordId(valuationRecordId: string): Promise<AttioValuationItemQueryResponse> {
    try {
      const response = await this.get<AttioValuationItemQueryResponse>(
        this.endpoints.retrieveValuationByRecordId(valuationRecordId)
      );
      return response;
    } catch (error) {
      this.handleError(error, 'getValuationByRecordId');
      throw error;
    }
  }
}
